import {
  LINE_PHOTO,
  PHONE_MSG,
  PHOTO_ASSIGN_MSG,
  RFQ_MSG,
  TRASH_MSG,
  isPhoneRole,
  parsePhoneQuantity,
  phoneQty,
  prettyFormula,
  type AxisNode,
  type Estimate,
  type EstimateLine,
  type EstimateNote,
  type LineMemo,
  type LineMemoList,
  type LinePhoto,
  type LinePhotoList,
  type PhoneLinePatch,
  type PhoneLineRestore,
  type PhoneLineRole,
  type ItemList,
  type PhotoAssignUndoResult,
  type Suggest,
  type TakeoffList,
  TAX_CATEGORIES,
  TAX_CATEGORY_SHORT,
  TAX_MSG,
  shippingReducedWarning,
  taxCategoryNorm,
  type TaxCategory,
  ESTIMATE_PC_MSG,
} from "@genbaryoku/contract";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { api, ApiError, messageOf } from "../../api.js";
import { useMe } from "../../auth.js";
import { BackButton, useLeaveGuard, usePageName } from "../../components/BackButton.jsx";
import { EditingChip, useEditors } from "../../components/Editing.jsx";
import { Slot, hasModule } from "../../core/slots.jsx";
import { uses } from "../../core/uses.js";
import { useAxesLists, type AxesLists } from "../../components/EstimateAxes.jsx";
import { ErrorText, guardComposingEnter, useToast } from "../../components/ui.jsx";
import { notify } from "../../components/Notice.jsx";
import { UnitInput } from "../../components/UnitInput.jsx";
import { lineSpecLabel, t } from "../../labels.js";
import { useAboveKeyboard } from "../../lib/aboveKeyboard.js";
import { dataUrlOf, photoPair } from "../../lib/image.js";
import { switchView } from "../../lib/view.js";
import { PhIcon } from "./icons.jsx";
import { PhotoInput } from "./PhotoInput.jsx";
import { enqueueAssign, cancelQueued, enqueuePhoto, kickPhotoQueue, onPhotoQueue, useAssignQueue, usePhotoQueue, type QueuedPhoto } from "./photoQueue.js";
import { PhotoDrawer, type Decision, type DrawerPhoto } from "./PhotoDrawer.jsx";
import { trashedOf, useUntrash } from "../estimates/Trash.jsx";
import { TrashedStop } from "../../components/StopScreen.jsx";

/** {n} などを入れる */
const fill = (s: string, v: Record<string, string | number>) => s.replace(/\{(\w+)\}/g, (_, k: string) => String(v[k] ?? ""));
const GROUP_KEY = "gb.phone.groupBy";
/** none＝場所・{trade}を両方とも使わない会社（使う機能。#142 段3）：1つの帯 */
type GroupBy = "place" | "trade" | "none";
/** まとめ方は端末に覚える（閉じて開いても同じ）。読めない窓では場所 */
function loadGroupBy(): GroupBy {
  try {
    return localStorage.getItem(GROUP_KEY) === "trade" ? "trade" : "place";
  } catch {
    return "place";
  }
}
function saveGroupBy(v: GroupBy) {
  try {
    localStorage.setItem(GROUP_KEY, v);
  } catch {
    /* 覚えられなくても表示は変わる */
  }
}

/** 木の順（1段目 → その下の2段目）と「親 ＞ 子」の名前 */
function treeOf(nodes: AxisNode[]): { order: AxisNode[]; full: Map<string, string> } {
  const by = new Map(nodes.map((n) => [n.id, n]));
  const kids = (pid: string | null) => nodes.filter((n) => (n.parentId ?? null) === pid).sort((a, b) => a.sortNo - b.sortNo || a.name.localeCompare(b.name, "ja"));
  const order: AxisNode[] = [];
  for (const top of kids(null)) {
    order.push(top);
    order.push(...kids(top.id));
  }
  // 親の無い2段目（親が消えた）も最後に
  for (const n of nodes) if (!order.includes(n)) order.push(n);
  const full = new Map(order.map((n) => [n.id, n.parentId && by.get(n.parentId) ? `${by.get(n.parentId)!.name} ＞ ${n.name}` : n.name]));
  return { order, full };
}

type Group = { key: string; name: string; rows: EstimateLine[] };
/** 見出しの帯（場所か工種でまとめる。属性の無い行は「その他」。中は元の並び） */
function groupsOf(lines: EstimateLine[], by: GroupBy, axes: { places: AxisNode[]; trades: AxisNode[] }): Group[] {
  if (by === "none") return lines.length ? [{ key: "all", name: PHONE_MSG.allRows, rows: lines }] : [];
  const tree = treeOf(by === "place" ? axes.places : axes.trades);
  const attr = (l: EstimateLine) => (by === "place" ? l.placeId : l.tradeId);
  const known = new Set(tree.order.map((n) => n.id));
  const out: Group[] = [];
  for (const n of tree.order) {
    const rows = lines.filter((l) => attr(l) === n.id);
    if (rows.length) out.push({ key: n.id, name: tree.full.get(n.id) ?? n.name, rows });
  }
  const other = lines.filter((l) => !attr(l) || !known.has(attr(l)!));
  if (other.length) out.push({ key: "other", name: PHONE_MSG.other, rows: other });
  return out;
}

/** 外注先 未定（DB の v_estimate_phone_summary と同じ決まり）：明細で材工「外注」・外注先なし（#307：説明の行の仮は数えない） */
const vendorMissing = (l: EstimateLine) => !l.vendorPartnerId && l.role === "detail" && l.workType === "subcontract";

/** 行の写真の大きさの決まり（契約の LINE_PHOTO） */
async function preparePhotos(files: File[], estimateId: string, lineLineageId: string | null, label: string): Promise<{ queued: number; broken: number; items: QueuedPhoto[] }> {
  let queued = 0;
  let broken = 0;
  const items: QueuedPhoto[] = [];
  for (const f of files) {
    const pair = await photoPair(f, LINE_PHOTO);
    if (!pair) {
      broken++;
      continue;
    }
    const item: QueuedPhoto = { clientUuid: crypto.randomUUID(), estimateId, lineLineageId, display: pair.display, thumb: pair.thumb, preview: await dataUrlOf(pair.thumb), createdAt: Date.now() + queued, label };
    await enqueuePhoto(item);
    items.push(item);
    queued++;
  }
  return { queued, broken, items };
}

/**
 * S2 見積の土台（スマホ版、#117、Figma 38:181）。PC の見積の画面（Estimate.tsx）は触らない（同じ API・同じ見積）。
 *   頭（‹ 見積書・⋮・工事名・顧客・行 n・写真 n・外注先 未定 n・まとめ方）→「行に入れていない写真 n枚」→ 見出しの帯＋行カード → 下に固定の「＋ 行」「＋ 見出し」「撮る」。
 *   行は行ごとに保存（PATCH、0.5秒後）。版がぶつかったら（PC で丸ごと保存した）帯で知らせて読み直す。写真は送り待ちの列から送る
 */
export function PhoneEstimatePage() {
  const { id = "" } = useParams();
  const q = useQuery({ queryKey: ["estimate", id], queryFn: () => api<Estimate>("GET", `/estimates/${id}`), retry: false });
  const untrash = useUntrash();
  usePageName(q.data ? q.data.revision.subject || PHONE_MSG.title : null);
  if (q.isPending) return <p className="hint ph-page">読み込んでいます…</p>;
  // #200：ゴミ箱の見積書（止めの画面＋復元。ゴミ箱の一覧はパソコンだけ＝脇は一覧へ）
  const trashed = trashedOf(q.error);
  if (trashed) return <TrashedStop info={trashed} listTo="/estimates" onRestore={() => void untrash(id).then((r) => r && void q.refetch())} />;
  if (q.isError) {
    return (
      <div className="soonbox">
        <ErrorText>{messageOf(q.error)}</ErrorText>
        <p>
          <Link className="btn text" to="/estimates">見積書一覧へ</Link>
        </p>
      </div>
    );
  }
  return <Workbench est={q.data} />;
}

type Sheet =
  | { kind: "line"; lineId: string }
  | { kind: "newLine"; role: "detail" | "group" }
  | { kind: "unassigned" }
  | { kind: "note" }
  | { kind: "menu" }
  | { kind: "vendor" }
  | { kind: "discard" }
  | { kind: "discardRfq"; count: number };

function Workbench({ est }: { est: Estimate }) {
  const id = est.id;
  const qc = useQueryClient();
  const nav = useNavigate();
  const { toast, node: toastNode } = useToast();
  const lists = useAxesLists(id, { estimate: true }, (m) => toast(m, undefined, "error"));
  const photosQ = useQuery({ queryKey: ["estimate-photos", id], queryFn: () => api<LinePhotoList>("GET", `/estimates/${id}/photos`) });
  const takeoffsQ = useQuery({ queryKey: ["takeoffs", id], queryFn: () => api<TakeoffList>("GET", `/estimates/${id}/takeoffs`) });
  const me = useMe();
  /** 写真（撮る・写真の引き出し）はモジュール field の会社だけ（#142 段2）。行・数量・メモは どの会社も */
  const field = hasModule(me.data, "field");
  // 写真の引き出しの品目（#118）：先に読んでおく（電波が切れても引き出しで選べる）
  useQuery({ queryKey: ["items"], queryFn: () => api<ItemList>("GET", "/items"), staleTime: 30_000, enabled: field && (est.status === "draft" || !!est.revising) });
  const pending = usePhotoQueue(id);
  const [groupByWanted, setGroupByState] = useState<GroupBy>(loadGroupBy);
  // 使う機能（#142 段3）：OFF の軸ではまとめない（場所 → {trade} → 1つの帯の順に落とす）。覚えたまとめ方は変えない
  const groupChoices = (["place", "trade"] as const).filter((g) => uses(me.data, g === "place" ? "places" : "trades"));
  const groupBy: GroupBy = groupChoices.includes(groupByWanted as "place" | "trade") ? groupByWanted : (groupChoices[0] ?? "none");
  const takeoffOn = uses(me.data, "takeoff");
  const setGroupBy = (v: GroupBy) => {
    setGroupByState(v);
    saveGroupBy(v);
  };
  const [folded, setFolded] = useState<Set<string>>(new Set());
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [vendorParam, setVendorParam] = useSearchParams();
  /** 写真を大きく（lineId＝どの行から開いたか。「ほかの行へ」はその行の分だけ移す） */
  const [viewer, setViewer] = useState<{ photoId: string; lineId?: string | undefined } | { pending: string } | null>(null);
  /** 写真の引き出し（#118）：振り分ける写真 */
  const [drawer, setDrawer] = useState<DrawerPhoto[] | null>(null);
  const hand = me.data?.preferences?.drawerHand ?? "right";
  const assignQueue = useAssignQueue(id);
  const [reload, setReload] = useState(0);
  const editable = est.status === "draft" || !!est.revising;
  /** 編集中（#202、docs/82 §4）：直せる見積を開いている間だけ合図を送り、自分以外を頭のチップに（PC の見積書と同じ） */
  const editors = useEditors(est.id, editable);

  // ---- 行ごとの保存（同時に1つ。いつも最新の版で送る。ぶつかったら帯を出して読み直す） ----
  const version = useRef(est.updatedAt);
  const busy = useRef(0);
  /**
   * 離れるときの確かめ（#178、自動保存の共通の仕組みの useLeaveGuard）：
   *   ・送れなかった行の直し（つながらない・断られた。トーストで知らせたもの）があるまま離れる＝「続ける／破棄」。同じ行の同じ欄を送れたら消える
   *   ・送る前（欄の 0.5 秒の待ち）・送っている最中なら、送り終えてから移る（送れなければ同じ確かめ）。ページを閉じる・再読み込みはブラウザの確かめ
   *   つながらないときに直しを端末に残す形は作っていない（docs/16 #178）
   */
  const sheetSave = useRef<{ pending: () => boolean; flush: () => void } | null>(null);
  const unsentRef = useRef(new Set<string>());
  const [unsentN, setUnsentN] = useState(0);
  const markUnsent = useCallback((key: string | null, on: boolean) => {
    const set = unsentRef.current;
    if (key == null) set.clear();
    else if (on) set.add(key);
    else set.delete(key);
    setUnsentN(set.size);
  }, []);
  const leaveGuard = useLeaveGuard({
    unsent: unsentN > 0,
    pending: () => busy.current > 0 || !!sheetSave.current?.pending(),
    atRisk: () => busy.current > 0 || !!sheetSave.current?.pending(),
    settle: async () => {
      sheetSave.current?.flush();
      for (let k = 0; k < 200 && busy.current > 0; k++) await new Promise((r) => setTimeout(r, 50));
      return busy.current === 0 && unsentRef.current.size === 0;
    },
  });
  const chain = useRef<Promise<void>>(Promise.resolve());
  const wantRefetch = useRef(false);
  useEffect(() => {
    if (!busy.current) version.current = est.updatedAt;
  }, [est.updatedAt]);
  const refetchAll = useCallback(() => {
    if (busy.current) {
      wantRefetch.current = true;
      return;
    }
    void qc.invalidateQueries({ queryKey: ["estimate", id] });
    void qc.invalidateQueries({ queryKey: ["estimate-photos", id] });
    void qc.invalidateQueries({ queryKey: ["takeoffs", id] });
    void qc.invalidateQueries({ queryKey: ["estimates"] });
  }, [qc, id]);
  /** 下書きの削除（#200：1回目の下書きはゴミ箱へ＝一覧で「元に戻す」）。送った見積依頼があれば、取消して削除するかを聞く */
  const discard = async (cancelRfqs: boolean): Promise<void> => {
    try {
      await api("DELETE", `/estimates/${id}${cancelRfqs ? "?cancelRfqs=1" : ""}`);
      void qc.invalidateQueries({ queryKey: ["estimates"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      if (est.revising) {
        setSheet(null);
        refetchAll();
      } else {
        qc.removeQueries({ queryKey: ["estimate", id] });
        nav("/estimates", { replace: true, state: { trashed: { id } } });
      }
    } catch (e) {
      if (e instanceof ApiError && e.code === "ESTIMATE_HAS_RFQ") {
        setSheet({ kind: "discardRfq", count: typeof e.problem.count === "number" ? e.problem.count : 1 });
        return;
      }
      toast(messageOf(e), undefined, "error");
      setSheet(null);
    }
  };
  const run = useCallback(
    <T,>(call: (version: string) => Promise<T>, pick: (r: T) => Estimate | undefined): Promise<T | undefined> => {
      busy.current++;
      const p = chain.current.then(async () => {
        await qc.cancelQueries({ queryKey: ["estimate", id] });
        try {
          const r = await call(version.current);
          const e = pick(r);
          if (e) {
            version.current = e.updatedAt;
            qc.setQueryData(["estimate", id], e);
          }
          return r;
        } catch (err) {
          if (err instanceof ApiError && err.code === "ESTIMATE_STALE") {
            // 読み直した知らせは通知（#168：前は × で閉じる帯）
            notify({ key: "phone-stale", tone: "warn", text: PHONE_MSG.stale, testId: "phone-stale" });
            setReload((n) => n + 1);
            wantRefetch.current = true;
            markUnsent(null, false); // 読み直した（シートも作り直す）＝送れなかった直しは知らせたうえで捨てた
          } else {
            toast(messageOf(err), undefined, "error");
          }
          return undefined;
        } finally {
          busy.current--;
          if (!busy.current && wantRefetch.current) {
            wantRefetch.current = false;
            refetchAll();
          }
        }
      });
      chain.current = p.then(() => undefined);
      return p;
    },
    [qc, id, toast, refetchAll, markUnsent],
  );
  const patchLine = useCallback(
    (lineRef: string, fields: Omit<PhoneLinePatch, "version">) => {
      // 送れなかった直しの印（行 × 欄）。同じ行の同じ欄を送れたら消す（#178）
      const key = `${lineRef}:${Object.keys(fields).sort().join(",")}`;
      return run(
        (v) =>
          api<Estimate>("PATCH", `/estimates/${id}/lines/${lineRef}`, { version: v, ...fields }).catch((err: unknown) => {
            if (!(err instanceof ApiError && err.code === "ESTIMATE_STALE")) markUnsent(key, true);
            throw err;
          }),
        (e) => e,
      ).then((e) => {
        if (e) markUnsent(key, false);
        if (e && fields.quantityText !== undefined) void qc.invalidateQueries({ queryKey: ["takeoffs", id] });
        return e;
      });
    },
    [run, id, qc, markUnsent],
  );

  // ---- 写真の送り待ち：開いたら続きを送る。送れたら一覧を読み直す ----
  // ---- 写真の引き出しの決定（#118）：送り待ちの列から送れたら答え（assignId）を覚える。送っている最中に「元に戻す」を押されていたら、届いてから元に戻す ----
  const assigned = useRef(new Map<string, string>());
  const undoWanted = useRef(new Set<string>());
  const undoAssign = useCallback(
    async (assignId: string) => {
      let failed = false;
      const r = await run(
        async (v) => {
          try {
            return await api<PhotoAssignUndoResult>("POST", `/estimates/${id}/photo-assign/${assignId}/undo`, { version: v });
          } catch (err) {
            if (err instanceof ApiError && err.status === 409) {
              failed = true;
              return undefined;
            }
            throw err;
          }
        },
        (x) => x?.estimate,
      );
      if (failed) {
        toast(PHOTO_ASSIGN_MSG.undoFailed, undefined, "error");
        return;
      }
      if (!r) return;
      qc.setQueryData(["estimate-photos", id], r.photos);
      void qc.invalidateQueries({ queryKey: ["takeoffs", id] });
      void qc.invalidateQueries({ queryKey: ["estimate-places", id] });
      void qc.invalidateQueries({ queryKey: ["estimates"] });
      toast(PHOTO_ASSIGN_MSG.undone);
    },
    [run, id, qc, toast],
  );
  useEffect(() => {
    void kickPhotoQueue();
    return onPhotoQueue((e) => {
      if (e.estimateId !== id) return;
      // 断られた知らせは列（photoQueue）がどの画面にいても右下に出す（#178。ここで出すと2つになる）
      if (e.kind === "assigned") {
        assigned.current.set(e.clientUuid, e.result.assignId);
        if (!busy.current) {
          version.current = e.result.estimate.updatedAt;
          qc.setQueryData(["estimate", id], e.result.estimate);
        } else wantRefetch.current = true;
        qc.setQueryData(["estimate-photos", id], e.result.photos);
        void qc.invalidateQueries({ queryKey: ["takeoffs", id] });
        void qc.invalidateQueries({ queryKey: ["estimate-places", id] });
        void qc.invalidateQueries({ queryKey: ["items"] });
        void qc.invalidateQueries({ queryKey: ["estimates"] });
        if (undoWanted.current.delete(e.clientUuid)) void undoAssign(e.result.assignId);
        return;
      }
      void qc.invalidateQueries({ queryKey: ["estimate-photos", id] });
      if (busy.current) wantRefetch.current = true;
      else void qc.invalidateQueries({ queryKey: ["estimate", id] });
    });
  }, [id, qc, undoAssign]);
  /** 列に入れる写真・振り分けに付ける件名（断られたときの知らせに出す。#178） */
  const queueLabel = est.revision.subject;
  const shoot = useCallback(
    async (files: File[], lineLineageId: string | null) => {
      const r = await preparePhotos(files, id, lineLineageId, queueLabel);
      if (r.broken) toast(PHONE_MSG.photoBroken);
      return r.items;
    },
    [id, toast, queueLabel],
  );
  /** 撮る（下の帯）：撮った写真をそのまま引き出しで振り分ける（出した見積は行に入れていない写真へ） */
  const shootAndAssign = useCallback(
    async (files: File[]) => {
      const items = await shoot(files, null);
      if (editable && items.length) setDrawer(items.map((x) => ({ key: x.clientUuid, src: x.preview, clientUuid: x.clientUuid })));
    },
    [shoot, editable],
  );
  /** 決定：送り待ちの列に積む（電波が無くても進める）。トーストの「元に戻す」は、まだ送っていなければ列から外し、送ったあとなら API で元に戻す */
  const decide = useCallback(
    async (d: Decision) => {
      setDrawer(null);
      const clientUuid = crypto.randomUUID();
      await enqueueAssign({ clientUuid, estimateId: id, payload: d.payload, ...(queueLabel ? { label: queueLabel } : {}) });
      const text = typeof navigator !== "undefined" && navigator.onLine === false ? PHOTO_ASSIGN_MSG.queued : fill(PHOTO_ASSIGN_MSG.assigned, { place: d.placeLabel, n: d.lineCount });
      toast(text, {
        label: PHONE_MSG.undo,
        onClick: () => {
          void (async () => {
            if (await cancelQueued(clientUuid)) {
              toast(PHOTO_ASSIGN_MSG.undone);
              return;
            }
            const aid = assigned.current.get(clientUuid);
            if (aid) void undoAssign(aid);
            else undoWanted.current.add(clientUuid);
          })();
        },
      });
    },
    [id, toast, undoAssign, queueLabel],
  );

  const axes = { places: est.axes?.places ?? [], trades: est.axes?.trades ?? [] };
  // {vendor}で絞る（#119、docs/56 §6.7）：?vendor=<id>／none（{vendor}なし）。見出しの行は出さない。戻るで戻る
  const vf = vendorParam.get("vendor");
  const vendorRows = est.lines.filter((l) => l.role === "detail" || l.role === "note");
  const shownLines = useMemo(
    () => (vf ? est.lines.filter((l) => (l.role === "detail" || l.role === "note") && (vf === "none" ? !l.vendorPartnerId : l.vendorPartnerId === vf)) : est.lines),
    [est.lines, vf],
  );
  const groups = useMemo(() => groupsOf(shownLines, groupBy, axes), [shownLines, groupBy, est.axes]); // eslint-disable-line react-hooks/exhaustive-deps
  /** #199（docs/80）：不使用の内訳の行。スマホには内訳のタブが無いので、行に「不使用」の印を付けて薄く出す（合計には入らない。複製・利用しないは PC） */
  const unusedSheets = new Set(est.sheets.filter((x) => x.type === "breakdown" && x.inUse === false).map((x) => x.id));
  const setVendorFilter = (v: string | null) => {
    setSheet(null);
    setVendorParam((p) => {
      const n = new URLSearchParams(p);
      if (v) n.set("vendor", v);
      else n.delete("vendor");
      return n;
    });
  };
  const photos = photosQ.data?.items ?? [];
  const photosOf = (lineId: string) => photos.filter((p) => p.lineIds.includes(lineId));
  const pendingOf = (lineageId: string | undefined) => pending.filter((p) => !!lineageId && p.lineLineageId === lineageId);
  const unassigned = photos.filter((p) => !p.lineIds.length);
  const lineageIds = new Set(est.lines.map((l) => l.lineageId));
  const unassignedPending = pending.filter((p) => !p.lineLineageId || !lineageIds.has(p.lineLineageId));
  const s = est.phoneSummary;
  const tkOf = (lineId: string) => (takeoffsQ.data?.items ?? []).filter((x) => x.lineId === lineId);
  const vendorName = (vid: string | null) => (vid ? (lists.vendors.find((v) => v.id === vid)?.name ?? "") : "");

  const deleteLine = async (l: EstimateLine) => {
    setSheet(null);
    const r = await run(
      (v) => api<{ estimate: Estimate; restore: PhoneLineRestore }>("DELETE", `/estimates/${id}/lines/${l.id}?version=${encodeURIComponent(v)}`),
      (x) => x.estimate,
    );
    if (!r) return;
    void qc.invalidateQueries({ queryKey: ["estimate-photos", id] });
    toast(fill(PHONE_MSG.deleted, { name: l.name || PHONE_MSG.kindLabel[l.role as PhoneLineRole] }), {
      label: PHONE_MSG.undo,
      onClick: () => {
        void run(
          (v) => api<Estimate>("POST", `/estimates/${id}/lines/restore`, { ...r.restore, version: v }),
          (e) => e,
        ).then(() => void qc.invalidateQueries({ queryKey: ["estimate-photos", id] }));
      },
    });
  };
  /** 上へ／下へ：同じ見出しの帯の中のとなりと入れ替える */
  const neighbor = (l: EstimateLine, dir: -1 | 1): EstimateLine | undefined => {
    const g = groups.find((x) => x.rows.some((r) => r.id === l.id));
    const rows = (g?.rows ?? []).filter((r) => isPhoneRole(r.role));
    const i = rows.findIndex((r) => r.id === l.id);
    return rows[i + dir];
  };

  const openLine = sheet?.kind === "line" ? est.lines.find((l) => l.id === sheet.lineId) : undefined;
  /** T2：送料の行を軽減にしている（頭の帯の下にお知らせの1行。止めない） */
  const shippingWarn = editable ? est.lines.map((l) => shippingReducedWarning(l.name, l.taxCategory)).find((x): x is string => !!x) : undefined;

  return (
    <div className="ph-page ph-s2" data-testid="phone-estimate">
      {/* 頭 */}
      <div className="ph-head">
        <div className="ph-headrow">
          <BackButton fallback={{ to: "/estimates", label: PHONE_MSG.back }} />
          <span className="sp" />
          <button type="button" className="ph-iconbtn" aria-label={PHONE_MSG.more} aria-haspopup="menu" aria-expanded={sheet?.kind === "menu"} onClick={() => setSheet({ kind: "menu" })} data-testid="phone-more">
            <PhIcon k="more" />
          </button>
        </div>
        <h1 className="ph-subj">{est.revision.subject || t("（{subject}はまだ）")}</h1>
        <p className="ph-client">
          {est.client.name} {est.client.honorific}
        </p>
        <div className="ph-chips" data-testid="phone-counts">
          <EditingChip editors={editors} />
          {/* 出した見積（#171。前は題の下の帯「出した見積です…」）：題の下の小さなチップ。変えられない所は title と読み上げ */}
          {!editable ? (
            <span className="chip ai" title={t(field ? PHONE_MSG.readOnly : PHONE_MSG.readOnlyNoPhoto)} data-testid="phone-issued">
              {PHONE_MSG.issuedChip}
              <span className="sr">：{t(field ? PHONE_MSG.readOnly : PHONE_MSG.readOnlyNoPhoto)}</span>
            </span>
          ) : null}
          <span className="chip" data-testid="count-lines">{fill(PHONE_MSG.lines, { n: s?.lineCount ?? 0 })}</span>
          {field ? (
            <span className="chip" data-testid="count-photos">
              <PhIcon k="camera" />
              {fill(PHONE_MSG.photos, { n: s?.photoCount ?? 0 })}
            </span>
          ) : null}
          {s && s.vendorMissingCount > 0 ? (
            <span className="chip warn" data-testid="count-vendor-missing">
              <PhIcon k={field ? "hat" : "people"} />
              {fill(t(PHONE_MSG.vendorMissingCount), { n: s.vendorMissingCount })}
            </span>
          ) : null}
          {vendorRows.some((l) => l.vendorPartnerId) ? (
            <button type="button" className={"chip" + (vf ? " is-on" : "")} aria-haspopup="dialog" onClick={() => setSheet({ kind: "vendor" })} data-testid="phone-vendor-filter">
              <PhIcon k={field ? "hat" : "people"} />
              {vf ? (vf === "none" ? t(RFQ_MSG.phoneFilterNone) : vendorName(vf)) : t(RFQ_MSG.phoneFilter)}
            </button>
          ) : null}
          {pending.length ? (
            <span className="chip ai" data-testid="count-pending">
              <PhIcon k="clock" />
              {fill(PHONE_MSG.pending, { n: pending.length })}
            </span>
          ) : null}
          {assignQueue.length ? (
            <span className="chip ai" data-testid="count-pending-assign">
              <PhIcon k="clock" />
              {fill(PHOTO_ASSIGN_MSG.pendingAssign, { n: assignQueue.length })}
            </span>
          ) : null}
        </div>
        {groupChoices.length > 1 ? (
          <div className="ph-groupby">
            <span className="lbl">{PHONE_MSG.groupBy}</span>
            <div className="ph-seg" role="group" aria-label={PHONE_MSG.groupBy}>
              {groupChoices.map((g) => (
                <button key={g} type="button" aria-pressed={groupBy === g} onClick={() => setGroupBy(g)} data-testid={`groupby-${g}`}>
                  {g === "place" ? PHONE_MSG.place : t(PHONE_MSG.trade)}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
      {shippingWarn ? (
        <p className="ph-notice" role="status" data-testid="phone-shipping-reduced">{shippingWarn}</p>
      ) : null}
      {photosQ.isPending && (s?.unassignedPhotoCount ?? 0) + unassignedPending.length > 0 ? (
        // 写真の一覧が届くまで（#179）：見積の数（phoneSummary）で同じ帯を押せない形で先に出す（後から入ると行のカードが帯の高さぶんずれる）
        <div className="ph-unassigned" aria-busy="true" data-testid="phone-unassigned-loading">
          <PhIcon k="camera" />
          <b>{fill(PHONE_MSG.unassignedPhotos, { n: (s?.unassignedPhotoCount ?? 0) + unassignedPending.length })}</b>
          <PhIcon k="chevR" />
        </div>
      ) : unassigned.length + unassignedPending.length > 0 ? (
        <button type="button" className="ph-unassigned" onClick={() => setSheet({ kind: "unassigned" })} data-testid="phone-unassigned">
          <PhIcon k="camera" />
          <b>{fill(PHONE_MSG.unassignedPhotos, { n: unassigned.length + unassignedPending.length })}</b>
          {[...unassigned.slice(0, 2).map((p) => p.thumbUrl), ...unassignedPending.slice(0, Math.max(0, 2 - unassigned.length)).map((p) => p.preview)].map((src, i) => (
            <img key={i} src={src} alt="" />
          ))}
          <PhIcon k="chevR" />
        </button>
      ) : null}

      {/* 行 */}
      <div className="ph-lines" data-testid="phone-lines">
        {!est.lines.length ? <p className="ph-empty">{field ? PHONE_MSG.emptyLines : PHONE_MSG.emptyLinesNoPhoto}</p> : null}
        {groups.map((g) => {
          const isFolded = folded.has(g.key);
          return (
            <section key={g.key} className="ph-group" data-testid="phone-group">
              <button
                type="button"
                className="ph-gband"
                aria-expanded={!isFolded}
                onClick={() => setFolded((f) => { const n = new Set(f); if (n.has(g.key)) n.delete(g.key); else n.add(g.key); return n; })}
                data-testid="phone-group-band"
              >
                <b>{g.name}</b>
                <span className="n">{fill(PHONE_MSG.rowsInGroup, { n: g.rows.length })}</span>
                <PhIcon k="chevD" className={isFolded ? "folded" : ""} />
              </button>
              {isFolded
                ? null
                : g.rows.map((l) => (
                    <LineCard
                      key={l.id}
                      l={l}
                      photos={photosOf(l.id)}
                      pending={pendingOf(l.lineageId)}
                      formula={l.quantitySource === "takeoff" ? (tkOf(l.id).length === 1 ? prettyFormula(tkOf(l.id)[0]!.formula) : tkOf(l.id).length > 1 ? fill(PHONE_MSG.takeoffMany, { n: tkOf(l.id).length }) : "") : ""}
                      vendor={vendorName(l.vendorPartnerId)}
                      unused={!!l.sheetId && unusedSheets.has(l.sheetId)}
                      onOpen={() => setSheet({ kind: "line", lineId: l.id })}
                      onShoot={field ? (files) => void shoot(files, l.lineageId ?? null) : undefined}
                    />
                  ))}
            </section>
          );
        })}
      </div>

      {/* 下に固定 */}
      <div className="ph-fixbar" data-notice-above="">
        {editable ? (
          <>
            <button type="button" className="btn ph-add" onClick={() => setSheet({ kind: "newLine", role: "detail" })} data-testid="phone-add-line">
              <PhIcon k="plus" />
              {PHONE_MSG.addLine}
            </button>
            <button type="button" className="btn ph-add wide" onClick={() => setSheet({ kind: "newLine", role: "group" })} data-testid="phone-add-heading">
              <PhIcon k="plus" />
              {PHONE_MSG.addHeading}
            </button>
          </>
        ) : null}
        {/* 「撮影」は差し込み口（モジュール field の会社だけ。products/field） */}
        {me.data ? <Slot name="phone.shoot" me={me.data} onFiles={(f) => void shootAndAssign(f)} /> : null}
      </div>

      {sheet?.kind === "line" && openLine ? (
        <LineSheet
          key={`${openLine.id}:${reload}`}
          est={est}
          line={openLine}
          editable={editable && isPhoneRole(openLine.role)}
          lists={lists}
          photos={photosOf(openLine.id)}
          pending={pendingOf(openLine.lineageId)}
          takeoffs={tkOf(openLine.id)}
          canUp={!!neighbor(openLine, -1)}
          canDown={!!neighbor(openLine, 1)}
          onPatch={(f) => patchLine(openLine.id, f)}
          onMove={(dir) => {
            const other = neighbor(openLine, dir);
            if (other) void patchLine(openLine.id, { swapWith: other.id });
          }}
          onDelete={() => void deleteLine(openLine)}
          onShoot={field ? (files) => void shoot(files, openLine.lineageId ?? null) : undefined}
          places={uses(me.data, "places")}
          trades={uses(me.data, "trades")}
          takeoff={takeoffOn}
          taxCol={uses(me.data, "reducedTax")}
          onOpenPhoto={(pid) => setViewer({ photoId: pid, lineId: openLine.id })}
          onOpenPending={(cid) => setViewer({ pending: cid })}
          onMemo={() => void qc.invalidateQueries({ queryKey: ["estimate", id] })}
          onRegister={(h) => {
            sheetSave.current = h;
          }}
          onClose={() => setSheet(null)}
        />
      ) : null}
      {sheet?.kind === "newLine" ? (
        <NewLineSheet
          role={sheet.role}
          onClose={() => setSheet(null)}
          onCreate={async (name) => {
            const r = await run(
              (v) => api<{ estimate: Estimate; lineId: string }>("POST", `/estimates/${id}/lines/provisional`, { version: v, name, role: sheet.role }),
              (x) => x.estimate,
            );
            if (!r) return;
            setSheet(sheet.role === "detail" ? { kind: "line", lineId: r.lineId } : null);
          }}
        />
      ) : null}
      {sheet?.kind === "unassigned" ? (
        <UnassignedSheet
          photos={unassigned}
          pending={unassignedPending}
          memos={photosQ.data?.unassignedMemos ?? []}
          canAssign={editable}
          onOpen={(pid) => setViewer({ photoId: pid })}
          onOpenPending={(cid) => setViewer({ pending: cid })}
          onAssign={(sel) => {
            setSheet(null);
            setDrawer(sel);
          }}
          onClose={() => setSheet(null)}
        />
      ) : null}
      {sheet?.kind === "note" ? <NoteSheet estimateId={id} onClose={() => setSheet(null)} onSaved={() => toast(PHONE_MSG.noteSaved)} /> : null}
      {sheet?.kind === "menu" ? (
        <MenuSheet
          canDiscard={editable}
          onClose={() => setSheet(null)}
          onPc={() => switchView("pc")}
          onNote={() => setSheet({ kind: "note" })}
          onDiscard={() => setSheet({ kind: "discard" })}
        />
      ) : null}
      {sheet?.kind === "vendor" ? (
        <SheetFrame title={t(RFQ_MSG.phoneFilter)} onClose={() => setSheet(null)} testId="phone-vendor-sheet">
          <ul className="ph-menu" role="menu">
            <li>
              <button type="button" role="menuitemradio" aria-checked={!vf} onClick={() => setVendorFilter(null)} data-testid="phone-vendor-all">
                {RFQ_MSG.phoneFilterAll}（{vendorRows.length}行）
              </button>
            </li>
            {vendorRows.some((l) => !l.vendorPartnerId) ? (
              <li>
                <button type="button" role="menuitemradio" aria-checked={vf === "none"} onClick={() => setVendorFilter("none")} data-testid="phone-vendor-none">
                  {t(RFQ_MSG.phoneFilterNone)}（{vendorRows.filter((l) => !l.vendorPartnerId).length}行）
                </button>
              </li>
            ) : null}
            {[...new Set(vendorRows.map((l) => l.vendorPartnerId).filter((x): x is string => !!x))].map((vid) => (
              <li key={vid}>
                <button type="button" role="menuitemradio" aria-checked={vf === vid} onClick={() => setVendorFilter(vid)} data-testid="phone-vendor-pick">
                  {vendorName(vid) || "—"}（{vendorRows.filter((l) => l.vendorPartnerId === vid).length}行）
                </button>
              </li>
            ))}
          </ul>
        </SheetFrame>
      ) : null}
      {sheet?.kind === "discard" ? (
        <ConfirmSheet
          title={PHONE_MSG.discard}
          // #200：1回目の下書きはゴミ箱へ（30日。パソコンのゴミ箱から復元）。2回目からの下書きは今のまま、その回だけ
          text={est.revising ? "修正中の回の下書きを捨てます。出した回は変わりません。" : TRASH_MSG.phoneAsk}
          okLabel={PHONE_MSG.discard}
          onClose={() => setSheet(null)}
          onOk={() => discard(false)}
        />
      ) : null}
      {sheet?.kind === "discardRfq" ? (
        // #200：送った見積依頼があれば、パソコンと同じく取消してから削除するか聞く
        <ConfirmSheet title={PHONE_MSG.discard} text={t(RFQ_MSG.hasRfq).replace("{n}", String(sheet.count))} okLabel={RFQ_MSG.cancelAndDelete} onClose={() => setSheet(null)} onOk={() => discard(true)} />
      ) : null}
      {viewer ? (
        <PhotoViewer
          est={est}
          estimateId={id}
          photo={"photoId" in viewer ? photos.find((p) => p.id === viewer.photoId) : undefined}
          fromLineId={"photoId" in viewer ? viewer.lineId : undefined}
          pendingItem={"pending" in viewer ? pending.find((p) => p.clientUuid === viewer.pending) : undefined}
          groups={groups}
          onClose={() => setViewer(null)}
          onChanged={(list, msg) => {
            qc.setQueryData(["estimate-photos", id], list);
            if (busy.current) wantRefetch.current = true;
            else void qc.invalidateQueries({ queryKey: ["estimate", id] });
            if (msg) toast(msg);
          }}
        />
      ) : null}
      {drawer ? <PhotoDrawer est={est} lists={lists} photos={drawer} hand={hand} onClose={() => setDrawer(null)} onDecide={(d) => void decide(d)} /> : null}
      {leaveGuard.dialog}
      {toastNode}
    </div>
  );
}

/** 行カード（Figma「スマホ/行カード」）：名称・規格・数量と単位（式は薄く）・しるし（仮・メモ・外注先）・写真（1枚＋枚数）／点線の「撮る」 */
function LineCard({ l, photos, pending, formula, vendor, unused = false, onOpen, onShoot }: { l: EstimateLine; photos: LinePhoto[]; pending: QueuedPhoto[]; formula: string; vendor: string; /** 不使用の内訳の行（#199） */ unused?: boolean; onOpen: () => void; /** 写真（field の会社だけ。無ければ写真の枠を出さない） */ onShoot: ((f: File[]) => void) | undefined }) {
  /** ヘルメットの印は現場力（field）の会社だけ。ほかは人の形（#142 段6 参考） */
  const field = hasModule(useMe().data, "field");
  const phone = isPhoneRole(l.role);
  const n = photos.length + pending.length;
  const thumb = photos[0]?.thumbUrl ?? pending[0]?.preview;
  if (!phone) {
    // PC で作った 小計・率・別途・値引き の行は見るだけ
    return (
      <div className={"ph-line pconly" + (unused ? " is-unused" : "")} data-testid="phone-line" data-role={l.role}>
        <div className="ph-linemain">
          <span className="nm">{l.name || "（名前なし）"}</span>
          <span className="tags">
            {unused ? <span className="sheet-unused" data-testid="phone-line-unused">{ESTIMATE_PC_MSG.sheetUnused}</span> : null}
            <span className="chip">{PHONE_MSG.pcEdit}</span>
          </span>
        </div>
      </div>
    );
  }
  return (
    <div className={"ph-line" + (l.role === "group" ? " heading" : "") + (unused ? " is-unused" : "")} data-testid="phone-line" data-role={l.role}>
      <button type="button" className="ph-linemain" onClick={onOpen} data-testid="phone-line-open">
        <span className="nm">
          {l.lineState === "provisional" ? <span className="prov" data-testid="prov-mark">{PHONE_MSG.provisional}</span> : null}
          {l.name || "（名前なし）"}
        </span>
        {l.role === "group" ? <span className="sp2">{PHONE_MSG.kindLabel.group}</span> : null}
        {l.spec ? <span className="sp2">{l.spec}</span> : null}
        {l.role === "detail" ? (
          <span className="qty">
            <b className="num" data-testid="phone-line-qty">
              {/* 手で打つ 0 は空（まだ数量を入れていない仮の行。#118 の「採寸せずに決定」） */}
              {l.quantitySource === "manual" && !l.quantity ? "" : phoneQty(l.quantity)} {l.unit}
            </b>
            {formula ? <span className="num f">{formula}</span> : null}
          </span>
        ) : null}
        <span className="tags">
          {unused ? <span className="sheet-unused" data-testid="phone-line-unused">{ESTIMATE_PC_MSG.sheetUnused}</span> : null}
          {l.memoCount > 0 ? (
            <span className="chip" data-testid="memo-chip">
              <PhIcon k="msg" />
              {fill(PHONE_MSG.memoCount, { n: l.memoCount })}
            </span>
          ) : null}
          {vendor ? (
            <span className="chip ai" data-testid="vendor-chip">
              <PhIcon k={field ? "hat" : "people"} />
              {vendor}
            </span>
          ) : vendorMissing(l) ? (
            <span className="chip warn" data-testid="vendor-missing">
              <PhIcon k={field ? "hat" : "people"} />
              {t(PHONE_MSG.vendorMissing)}
            </span>
          ) : null}
        </span>
      </button>
      {l.role === "group" || !onShoot ? null : n ? (
        <button type="button" className="ph-thumb" onClick={onOpen} aria-label={fill(PHONE_MSG.photos, { n })} data-testid="phone-line-thumb">
          {thumb ? <img src={thumb} alt="" /> : <PhIcon k="image" />}
          <span className="cnt">{n}</span>
        </button>
      ) : (
        <PhotoInput capture className="ph-thumb ph-shoot" onFiles={onShoot} testId="phone-line-shoot" label={PHONE_MSG.shoot}>
          <PhIcon k="camera" />
          <span>{PHONE_MSG.shoot}</span>
        </PhotoInput>
      )}
    </div>
  );
}

/** 下から出るシートの枠（取っ手・題・右の文字のボタン）。外を押す・Esc で閉じる */

function SheetFrame({ title, action, onClose, children, full, testId, z }: { title: string; action?: ReactNode; onClose: () => void; children: ReactNode; full?: boolean; testId?: string; z?: number }) {
  const scrim = useRef<HTMLDivElement>(null);
  useAboveKeyboard(scrim);
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", k);
    document.body.classList.add("ph-sheet-open");
    return () => {
      document.removeEventListener("keydown", k);
      document.body.classList.remove("ph-sheet-open");
    };
  }, [onClose]);
  return (
    <div ref={scrim} className="ph-scrim" style={z ? { zIndex: z } : undefined} onClick={(e) => e.target === e.currentTarget && onClose()}>
      {/* #172：通知はシートの上の端より上に（背の高い .full は上に入らないので持ち上げない＝components/Notice の useNoticeLift） */}
      <div className={"ph-sheet" + (full ? " full" : "")} role="dialog" aria-modal="true" aria-label={title} data-testid={testId} data-notice-above="">
        <div className="ph-grip" aria-hidden="true" />
        <div className="ph-sheethead">
          <h2>{title}</h2>
          {action ?? (
            <button type="button" className="btn text" onClick={onClose}>
              {PHONE_MSG.close}
            </button>
          )}
        </div>
        <div className="ph-sheetbody">{children}</div>
      </div>
    </div>
  );
}

/**
 * 文字の欄の変更を 0.5秒後に送る（出るとき・「完了」・閉じるときはすぐ）。送る関数は毎回の描画で変わってよい（いつも最新を呼ぶ）。
 * 描き直しのたびに送らない（flush は変わらない＝閉じるときだけ残りを送る）
 */
function useDebounced(send: (v: string) => void, delay = 500) {
  const sendRef = useRef(send);
  sendRef.current = send;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const last = useRef<string | null>(null);
  const flush = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    if (last.current != null) {
      const v = last.current;
      last.current = null;
      sendRef.current(v);
    }
  }, []);
  const push = useCallback(
    (v: string) => {
      last.current = v;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(flush, delay);
    },
    [flush, delay],
  );
  useEffect(() => () => flush(), [flush]);
  /** まだ送っていない文字がある（離れるときの確かめ。#178） */
  const pending = useCallback(() => last.current != null, []);
  return useMemo(() => ({ push, flush, pending }), [push, flush, pending]);
}

/** 数量の欄の最初の文字：拾い出し1つなら式、手入力なら数（0 は空） */
function qtyTextOf(l: EstimateLine, takeoffs: TakeoffList["items"]): string {
  if (l.quantitySource === "takeoff" && takeoffs.length === 1) return takeoffs[0]!.formula;
  return l.quantity ? phoneQty(l.quantity) : "";
}
/** 全角の数字・小数点は半角に（式の × ÷ ＋ は残す。docs/10 §5.2） */
const halfDigits = (s: string) => s.replace(/[０-９．]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0));

/**
 * S3 行のシート（下から全画面、Figma 39:194）：名称（候補）・規格・数量（式も）・単位・写真・社内メモ・外注先・場所・工種・行の種類・上へ／下へ・行を削除。
 * 出した見積・PC の行は読むだけ（写真・メモは足せる）
 */
function LineSheet(props: {
  est: Estimate;
  line: EstimateLine;
  editable: boolean;
  lists: AxesLists;
  photos: LinePhoto[];
  pending: QueuedPhoto[];
  takeoffs: TakeoffList["items"];
  canUp: boolean;
  canDown: boolean;
  onPatch: (f: Omit<PhoneLinePatch, "version">) => Promise<Estimate | undefined>;
  onMove: (dir: -1 | 1) => void;
  onDelete: () => void;
  /** 写真（field の会社だけ。無ければ写真の段を出さない） */
  onShoot: ((f: File[]) => void) | undefined;
  /** 使う機能（#142 段3）：場所・{trade}の欄、数量の式（{takeoff}。OFF なら数字だけ） */
  places: boolean;
  trades: boolean;
  takeoff: boolean;
  /** 使う機能「軽減税率・税区分」（T2）：ON は「税区分」の欄を出す、OFF は「その他」の中 */
  taxCol: boolean;
  onOpenPhoto: (photoId: string) => void;
  onOpenPending: (clientUuid: string) => void;
  onMemo: () => void;
  /** 送る前の欄があるかと、いま送る関数を親に渡す（離れるときの確かめ。#178）。閉じると null */
  onRegister?: (h: { pending: () => boolean; flush: () => void } | null) => void;
  onClose: () => void;
}) {
  const { line: l, editable, lists } = props;
  const shootFn = props.onShoot;
  const qc = useQueryClient();
  const [name, setName] = useState(l.name);
  const [spec, setSpec] = useState(l.spec);
  const [qty, setQty] = useState(() => qtyTextOf(l, props.takeoffs));
  /** 単位（#164）：会社の単位の一覧を候補に出す欄（components/UnitInput.tsx）。打った頭の文字で絞り、一覧に無い単位も打てる */
  const [unit, setUnit] = useState(l.unit);
  const [cands, setCands] = useState(false);
  const [newPlace, setNewPlace] = useState(false);
  const many = l.quantitySource === "takeoff" && props.takeoffs.length > 1;
  const sendName = useDebounced((v) => void props.onPatch({ name: v }));
  const sendSpec = useDebounced((v) => void props.onPatch({ spec: v }));
  const sendUnit = useDebounced((v) => void props.onPatch({ unit: v }));
  const sendQty = useDebounced((v) => {
    const k = parsePhoneQuantity(v).kind;
    // {takeoff}を使わない会社は数字だけ（式は拾い出しになるので送らない）
    if (k !== "invalid" && (props.takeoff || k !== "formula")) void props.onPatch({ quantityText: v });
  });
  const flushAll = () => {
    sendName.flush();
    sendSpec.flush();
    sendQty.flush();
    sendUnit.flush();
  };
  const flushAllRef = useRef(flushAll);
  flushAllRef.current = flushAll;
  const onRegister = props.onRegister;
  useEffect(() => {
    onRegister?.({ pending: () => sendName.pending() || sendSpec.pending() || sendQty.pending() || sendUnit.pending(), flush: () => flushAllRef.current() });
    return () => onRegister?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const pq = parsePhoneQuantity(qty);
  const memos = useQuery({ queryKey: ["line-memos", props.est.id, l.lineageId ?? l.id], queryFn: () => api<LineMemoList>("GET", `/estimates/${props.est.id}/lines/${l.lineageId ?? l.id}/memos`) });
  const [memo, setMemo] = useState("");
  const [memoErr, setMemoErr] = useState<string | undefined>();
  const addMemo = async () => {
    const body = memo.trim();
    if (!body) return;
    try {
      const r = await api<LineMemoList>("POST", `/estimates/${props.est.id}/lines/${l.lineageId ?? l.id}/memos`, { body });
      qc.setQueryData(["line-memos", props.est.id, l.lineageId ?? l.id], r);
      setMemo("");
      setMemoErr(undefined);
      props.onMemo();
    } catch (e) {
      setMemoErr(messageOf(e));
    }
  };
  const places = treeOf(props.est.axes?.places ?? []);
  const trades = treeOf(lists.trades.filter((x) => !x.archived).map((x) => ({ id: x.id, parentId: x.parentId, name: x.name, sortNo: x.sortNo })));
  const detail = l.role === "detail";
  const title = editable ? PHONE_MSG.editLine : PHONE_MSG.viewLine;
  /** T2：税区分（ふつうの行だけ）。選ぶとすぐ保存（行の PATCH） */
  const taxField = (
    <div className="field" data-testid="phone-tax-field">
      <select id="ph-tax" className="inp" value={taxCategoryNorm(l.taxCategory)} disabled={!editable} onChange={(e) => void props.onPatch({ taxCategory: e.target.value as TaxCategory })} data-testid="phone-tax">
        {TAX_CATEGORIES.map((c) => (
          <option key={c} value={c}>{TAX_CATEGORY_SHORT[c]}</option>
        ))}
      </select>
      <label htmlFor="ph-tax">{TAX_MSG.category}</label>
    </div>
  );
  const n = props.photos.length + props.pending.length;

  return (
    <SheetFrame
      title={title}
      full
      testId="phone-line-sheet"
      onClose={() => {
        flushAll();
        props.onClose();
      }}
      action={
        <button
          type="button"
          className="btn text"
          onClick={() => {
            flushAll();
            props.onClose();
          }}
          data-testid="phone-line-done"
        >
          {PHONE_MSG.done}
        </button>
      }
    >
      <div className="field ph-namefield">
        <input
          id="ph-name"
          className="inp"
          value={name}
          placeholder=" "
          disabled={!editable}
          maxLength={200}
          onChange={(e) => {
            setName(e.target.value);
            sendName.push(e.target.value);
          }}
          onBlur={sendName.flush}
          onKeyDown={guardComposingEnter}
        />
        <label htmlFor="ph-name">{l.role === "note" ? "文" : t(PHONE_MSG.name)}</label>
        {editable ? (
          <button type="button" className="ph-fieldbtn" aria-label={PHONE_MSG.candidates} onClick={() => { sendName.flush(); setCands(true); }} data-testid="phone-candidates-open">
            <PhIcon k="search" />
          </button>
        ) : null}
      </div>
      {l.role !== "group" ? (
        <div className="field">
          <input id="ph-spec" className="inp" value={spec} placeholder=" " disabled={!editable} maxLength={200} onChange={(e) => { setSpec(e.target.value); sendSpec.push(e.target.value); }} onBlur={sendSpec.flush} />
          <label htmlFor="ph-spec">{lineSpecLabel()}</label>
        </div>
      ) : null}
      {detail ? (
        <div className="ph-qtyrow">
          <div className="ph-qty">
            <div className="field">
              <input
                id="ph-qty"
                className="inp num"
                value={many ? "" : qty}
                placeholder={many ? fill(PHONE_MSG.takeoffMany, { n: props.takeoffs.length }) : " "}
                disabled={!editable || many}
                inputMode="decimal"
                autoComplete="off"
                onChange={(e) => {
                  const v = halfDigits(e.target.value);
                  setQty(v);
                  sendQty.push(v);
                }}
                onBlur={sendQty.flush}
                onKeyDown={guardComposingEnter}
                data-testid="phone-qty"
              />
              <label htmlFor="ph-qty">{PHONE_MSG.quantity}</label>
            </div>
            {pq.kind === "formula" && !props.takeoff ? (
              <span className="ph-err" role="alert" data-testid="phone-qty-number-only">{PHONE_MSG.numberOnly}</span>
            ) : pq.kind === "formula" ? (
              <b className="num ph-eq" data-testid="phone-qty-eq">{fill(PHONE_MSG.formulaEquals, { v: pq.value.toFixed(2) })}</b>
            ) : pq.kind === "invalid" && !many ? (
              <span className="ph-err" role="alert">{PHONE_MSG.formulaInvalid}</span>
            ) : many ? (
              <b className="num ph-eq">{fill(PHONE_MSG.formulaEquals, { v: (l.quantity ?? 0).toFixed(2) })}</b>
            ) : null}
          </div>
          <div className="field ph-unit">
            <UnitInput
              id="ph-unit"
              value={unit}
              placeholder=" "
              label={PHONE_MSG.unit}
              disabled={!editable}
              onChange={(v) => {
                setUnit(v);
                sendUnit.push(v);
              }}
              onBlur={sendUnit.flush}
              onKeyDown={guardComposingEnter}
              testId="phone-unit"
            />
            <label htmlFor="ph-unit">{PHONE_MSG.unit}</label>
          </div>
        </div>
      ) : null}

      {l.role !== "group" && shootFn ? (
        <>
          <div className="ph-phhead">
            <b>{fill(PHONE_MSG.photos, { n })}枚</b>
            <PhotoInput capture className="btn tonal" onFiles={shootFn} testId="phone-line-camera">
              <PhIcon k="camera" />
              {PHONE_MSG.shoot}
            </PhotoInput>
            <PhotoInput multiple className="btn" onFiles={shootFn} testId="phone-line-pick">
              <PhIcon k="image" />
              {PHONE_MSG.pick}
            </PhotoInput>
          </div>
          <div className="ph-grid" data-testid="phone-line-photos">
            {props.photos.map((p) => (
              <button key={p.id} type="button" className="ph-cell" onClick={() => props.onOpenPhoto(p.id)} aria-label={p.caption || "写真"}>
                <img src={p.thumbUrl} alt="" data-testid="phone-photo" />
              </button>
            ))}
            {props.pending.map((p) => (
              <button key={p.clientUuid} type="button" className="ph-cell pending" onClick={() => props.onOpenPending(p.clientUuid)} aria-label="送り待ちの写真">
                <img src={p.preview} alt="" />
                <span className="cnt"><PhIcon k="clock" /></span>
              </button>
            ))}
            <PhotoInput multiple className="ph-cell add" onFiles={shootFn} label={PHONE_MSG.add}>
              <PhIcon k="plus" />
              <span>{PHONE_MSG.add}</span>
            </PhotoInput>
          </div>
        </>
      ) : null}

      <b className="ph-sub">{PHONE_MSG.memoHeading}</b>
      <ul className="ph-memos" data-testid="phone-memos">
        {(memos.data?.items ?? []).map((m) => (
          <MemoItem key={m.id} m={m} />
        ))}
      </ul>
      <div className="field ph-memofield">
        <input
          id="ph-memo"
          className="inp"
          value={memo}
          placeholder=" "
          maxLength={1000}
          onChange={(e) => setMemo(e.target.value)}
          onKeyDown={(e) => {
            guardComposingEnter(e);
            if (e.key === "Enter" && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void addMemo();
            }
          }}
          data-testid="phone-memo-input"
        />
        <label htmlFor="ph-memo">{PHONE_MSG.addMemo}</label>
        <button type="button" className="ph-fieldbtn" aria-label={PHONE_MSG.sendMemo} onClick={() => void addMemo()} disabled={!memo.trim()} data-testid="phone-memo-send">
          <PhIcon k="send" />
        </button>
      </div>
      <ErrorText>{memoErr}</ErrorText>

      {l.role !== "group" ? (
        <div className="field">
          <select id="ph-vendor" className="inp" value={l.vendorPartnerId ?? ""} disabled={!editable} onChange={(e) => void props.onPatch({ vendorPartnerId: e.target.value || null })} data-testid="phone-vendor">
            <option value="">{t(PHONE_MSG.noVendor)}</option>
            {lists.vendors.map((v) => (
              <option key={v.id} value={v.id}>{v.label}</option>
            ))}
          </select>
          <label htmlFor="ph-vendor">{t(PHONE_MSG.vendor)}</label>
        </div>
      ) : null}
      <div className="ph-two">
        {props.places ? (
          <div className="field">
            <select
              id="ph-place"
              className="inp"
              value={l.placeId ?? ""}
              disabled={!editable}
              onChange={(e) => {
                if (e.target.value === "__new") {
                  setNewPlace(true);
                  return;
                }
                void props.onPatch({ placeId: e.target.value || null });
              }}
              data-testid="phone-place"
            >
              <option value="">{PHONE_MSG.noPlace}</option>
              {places.order.map((p) => (
                <option key={p.id} value={p.id}>{places.full.get(p.id)}</option>
              ))}
              <option value="__new">{PHONE_MSG.addPlace}</option>
            </select>
            <label htmlFor="ph-place">{PHONE_MSG.place}</label>
          </div>
        ) : null}
        {props.trades ? (
          <div className="field">
            <select id="ph-trade" className="inp" value={l.tradeId ?? ""} disabled={!editable} onChange={(e) => void props.onPatch({ tradeId: e.target.value || null })} data-testid="phone-trade">
              <option value="">{t(PHONE_MSG.noTrade)}</option>
              {trades.order.map((x) => (
                <option key={x.id} value={x.id}>{trades.full.get(x.id)}</option>
              ))}
              {l.tradeId && !trades.full.has(l.tradeId) ? <option value={l.tradeId}>{(props.est.axes?.trades ?? []).find((x) => x.id === l.tradeId)?.name ?? "—"}</option> : null}
            </select>
            <label htmlFor="ph-trade">{t(PHONE_MSG.trade)}</label>
          </div>
        ) : null}
      </div>
      {detail && props.taxCol ? taxField : null}
      {detail && !props.taxCol ? (
        <details className="ph-other" data-testid="phone-other">
          <summary>{PHONE_MSG.other}</summary>
          {taxField}
        </details>
      ) : null}
      {editable ? (
        <>
          <div className="ph-seg wide" role="group" aria-label={PHONE_MSG.kind} data-testid="phone-kind">
            {(["detail", "group", "note"] as const).map((r) => (
              <button
                key={r}
                type="button"
                aria-pressed={l.role === r}
                onClick={() => {
                  if (r === l.role) return;
                  flushAll();
                  void props.onPatch({ role: r });
                }}
              >
                {PHONE_MSG.kindLabel[r]}
              </button>
            ))}
          </div>
          <div className="ph-actions">
            <button type="button" className="btn" disabled={!props.canUp} onClick={() => { flushAll(); props.onMove(-1); }} data-testid="phone-up">
              <PhIcon k="up" />
              {PHONE_MSG.up}
            </button>
            <button type="button" className="btn" disabled={!props.canDown} onClick={() => { flushAll(); props.onMove(1); }} data-testid="phone-down">
              <PhIcon k="down" />
              {PHONE_MSG.down}
            </button>
            <span className="sp" />
            <button type="button" className="btn text danger" onClick={() => { flushAll(); props.onDelete(); }} data-testid="phone-delete">
              <PhIcon k="trash" />
              {PHONE_MSG.deleteLine}
            </button>
          </div>
        </>
      ) : null}
      {cands ? (
        <CandidateSheet
          est={props.est}
          query={name}
          onClose={() => setCands(false)}
          onPick={(c) => {
            setCands(false);
            setName(c.name);
            if (c.spec) setSpec(c.spec);
            const f: Omit<PhoneLinePatch, "version"> = { name: c.name, ...(c.spec ? { spec: c.spec } : {}) };
            if (detail && c.unit && !unit) {
              setUnit(c.unit);
              f.unit = c.unit;
            }
            void props.onPatch(f);
          }}
        />
      ) : null}
      {newPlace ? (
        <NewPlaceSheet
          onClose={() => setNewPlace(false)}
          onCreate={async (text) => {
            const pid = await lists.createPlace(text);
            setNewPlace(false);
            await qc.invalidateQueries({ queryKey: ["estimate", props.est.id] });
            void props.onPatch({ placeId: pid });
          }}
        />
      ) : null}
    </SheetFrame>
  );
}

function MemoItem({ m }: { m: LineMemo }) {
  const d = new Date(m.createdAt);
  const when = d.toLocaleString("ja-JP", { timeZone: "Asia/Tokyo", month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" });
  return (
    <li className="ph-memo" data-testid="phone-memo">
      <span className="who">
        {when} {m.createdByName}
      </span>
      <span className="body">{m.body}</span>
    </li>
  );
}

/** 「＋ 行」「＋ 見出し」：名称だけ聞いて作る */
function NewLineSheet({ role, onCreate, onClose }: { role: "detail" | "group"; onCreate: (name: string) => Promise<void>; onClose: () => void }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => ref.current?.focus(), []);
  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    await onCreate(name.trim());
    setBusy(false);
  };
  return (
    <SheetFrame title={role === "detail" ? PHONE_MSG.newLineTitle : PHONE_MSG.newHeadingTitle} onClose={onClose} testId="phone-new-line">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="field">
          <input ref={ref} id="ph-newname" className="inp" value={name} placeholder=" " maxLength={200} onChange={(e) => setName(e.target.value)} onKeyDown={guardComposingEnter} data-testid="phone-new-name" />
          <label htmlFor="ph-newname">{t(PHONE_MSG.newLineName)}</label>
        </div>
        <button type="submit" className="btn primary ph-wide" disabled={!name.trim() || busy} data-testid="phone-new-create">
          {role === "detail" ? PHONE_MSG.createLine : PHONE_MSG.createHeading}
        </button>
      </form>
    </SheetFrame>
  );
}

/** 「＋ 場所追加」：「2F ＞ 子供部屋」は2段で作る */
function NewPlaceSheet({ onCreate, onClose }: { onCreate: (text: string) => Promise<void>; onClose: () => void }) {
  const [text, setText] = useState("");
  const [err, setErr] = useState<string | undefined>();
  return (
    <SheetFrame title={PHONE_MSG.addPlace.replace("＋ ", "")} onClose={onClose} z={70} testId="phone-new-place">
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!text.trim()) return;
          try {
            await onCreate(text.trim());
          } catch (x) {
            setErr(x instanceof Error && !(x instanceof ApiError) ? x.message : messageOf(x));
          }
        }}
      >
        <div className="field">
          <input id="ph-newplace" className="inp" value={text} placeholder=" " maxLength={101} autoFocus onChange={(e) => setText(e.target.value)} onKeyDown={guardComposingEnter} data-testid="phone-new-place-name" />
          <label htmlFor="ph-newplace">{PHONE_MSG.placeName}</label>
        </div>
        <ErrorText>{err}</ErrorText>
        <button type="submit" className="btn primary ph-wide" disabled={!text.trim()} data-testid="phone-new-place-create">
          {PHONE_MSG.addPlace.replace("＋ ", "")}
        </button>
      </form>
    </SheetFrame>
  );
}

type Cand = { name: string; spec: string; unit: string; count?: number };
/** 名称の候補（PC の候補の引き出しと同じ出どころ：この見積の中・前の見積。単価は出さない） */
function CandidateSheet({ est, query, onPick, onClose }: { est: Estimate; query: string; onPick: (c: Cand) => void; onClose: () => void }) {
  const [q, setQ] = useState(query.trim());
  useEffect(() => {
    const t = setTimeout(() => setQ(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);
  // #179：打ち直しても前の候補を残して待つ（空になってから入り直すと、指の下の候補が入れ替わる）
  const past = useQuery({ queryKey: ["suggest", q, est.id], queryFn: () => api<Suggest>("GET", `/estimates/suggest?q=${encodeURIComponent(q)}&exclude=${est.id}`), placeholderData: keepPreviousData });
  const seen = new Set<string>();
  const here: Cand[] = [];
  for (const l of est.lines) {
    if (l.role !== "detail" || !l.name.trim()) continue;
    if (q && !l.name.includes(q) && !l.spec.includes(q)) continue;
    const k = `${l.name}|${l.spec}|${l.unit}`;
    if (seen.has(k)) continue;
    seen.add(k);
    here.push({ name: l.name, spec: l.spec, unit: l.unit });
  }
  const pastItems: Cand[] = (past.data?.past ?? []).map((p) => ({ name: p.name, spec: p.spec, unit: p.unit, count: p.count }));
  const row = (c: Cand, i: number) => (
    <li key={`${c.name}|${c.spec}|${c.unit}|${i}`}>
      <button type="button" className="ph-cand" onClick={() => onPick(c)} data-testid="phone-candidate">
        <b>{c.name}</b>
        <span className="m">{[c.spec, c.unit, c.count ? fill(PHONE_MSG.times, { n: c.count }) : ""].filter(Boolean).join("・")}</span>
      </button>
    </li>
  );
  return (
    // 高さは決まった形（full）：候補が届くたびにシートが上へ伸びて、上の「閉じる」・この見積の中の候補がずれないように（#179）
    <SheetFrame title={PHONE_MSG.candidates} onClose={onClose} z={70} full testId="phone-candidates">
      {here.length ? (
        <>
          <b className="ph-sub">{PHONE_MSG.candidatesHere}</b>
          <ul className="ph-candlist">{here.map(row)}</ul>
        </>
      ) : null}
      <b className="ph-sub">{PHONE_MSG.candidatesPast}</b>
      <ul className="ph-candlist">{pastItems.map(row)}</ul>
      {!here.length && !pastItems.length && past.data ? <p className="ph-empty">{PHONE_MSG.noCandidates}</p> : null}
    </SheetFrame>
  );
}

/**
 * 行に入れていない写真（と、行を消して行に入れていないメモ）。
 * #118：写真の右上の ✓ で選んで「振り分ける（n枚）」→ 写真の引き出し（場所 → 品目 → 採寸）。写真を押すと大きく
 */
function UnassignedSheet({ photos, pending, memos, canAssign, onOpen, onOpenPending, onAssign, onClose }: {
  photos: LinePhoto[];
  pending: QueuedPhoto[];
  memos: LineMemo[];
  canAssign: boolean;
  onOpen: (id: string) => void;
  onOpenPending: (cid: string) => void;
  onAssign: (sel: DrawerPhoto[]) => void;
  onClose: () => void;
}) {
  const [sel, setSel] = useState<string[]>([]);
  const toggle = (k: string) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const all: DrawerPhoto[] = [
    ...photos.map((p) => ({ key: p.id, src: p.thumbUrl, id: p.id })),
    ...pending.map((p) => ({ key: p.clientUuid, src: p.preview, clientUuid: p.clientUuid })),
  ];
  const check = (k: string) =>
    canAssign ? (
      <button type="button" className="ph-cellcheck" role="checkbox" aria-checked={sel.includes(k)} aria-label={PHOTO_ASSIGN_MSG.selectPhoto} onClick={() => toggle(k)} data-testid="phone-unassigned-select">
        <span className="box" aria-hidden="true">{sel.includes(k) ? <PhIcon k="check" /> : null}</span>
      </button>
    ) : null;
  return (
    <SheetFrame title={fill(PHONE_MSG.unassignedPhotos, { n: photos.length + pending.length })} onClose={onClose} full testId="phone-unassigned-sheet">
      <div className="ph-grid">
        {photos.map((p) => (
          <div key={p.id} className="ph-cellwrap">
            <button type="button" className="ph-cell" onClick={() => onOpen(p.id)} aria-label={p.caption || "写真"} data-testid="phone-unassigned-photo">
              <img src={p.thumbUrl} alt="" />
            </button>
            {check(p.id)}
          </div>
        ))}
        {pending.map((p) => (
          <div key={p.clientUuid} className="ph-cellwrap">
            <button type="button" className="ph-cell pending" onClick={() => onOpenPending(p.clientUuid)} aria-label="送り待ちの写真">
              <img src={p.preview} alt="" />
              <span className="cnt"><PhIcon k="clock" /></span>
            </button>
            {check(p.clientUuid)}
          </div>
        ))}
      </div>
      {canAssign && all.length ? (
        <button type="button" className="btn primary ph-wide" disabled={!sel.length} onClick={() => onAssign(all.filter((x) => sel.includes(x.key)))} data-testid="phone-unassigned-assign">
          {fill(PHOTO_ASSIGN_MSG.assignPhotos, { n: sel.length })}
        </button>
      ) : null}
      {memos.length ? (
        <>
          <b className="ph-sub">{fill(PHONE_MSG.unassignedMemos, { n: memos.length })}</b>
          <ul className="ph-memos">
            {memos.map((m) => (
              <MemoItem key={m.id} m={m} />
            ))}
          </ul>
        </>
      ) : null}
    </SheetFrame>
  );
}

/**
 * 写真を大きく：ひとこと・ほかの行へ（行に入れる）・写真を消す。送り待ちの写真は見るだけ。
 * #118：1枚が何行にも付く。行から開いたとき（fromLineId）の「ほかの行へ」はその行の分だけ移す（ほかの行には付いたまま）
 */
function PhotoViewer({ est, estimateId, photo, fromLineId, pendingItem, groups, onClose, onChanged }: {
  est: Estimate;
  estimateId: string;
  photo: LinePhoto | undefined;
  fromLineId: string | undefined;
  pendingItem: QueuedPhoto | undefined;
  groups: Group[];
  onClose: () => void;
  onChanged: (list: LinePhotoList, msg?: string) => void;
}) {
  const [caption, setCaption] = useState(photo?.caption ?? "");
  const [picking, setPicking] = useState(false);
  const [sure, setSure] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  const send = useCallback(
    async (body: Record<string, unknown>, msg?: string) => {
      if (!photo) return;
      try {
        const r = await api<LinePhotoList>("PUT", `/estimates/${estimateId}/photos/${photo.id}`, body);
        onChanged(r, msg);
      } catch (e) {
        setErr(messageOf(e));
      }
    },
    [photo, estimateId, onChanged],
  );
  const sendCaption = useDebounced((v) => void send({ caption: v }));
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onClose]);
  if (!photo && !pendingItem) return null;
  const lineName = (lineId: string | null) => est.lines.find((l) => l.id === lineId)?.name ?? "";
  /** いま見ている行（行から開いたときはその行、それ以外は付いている最初の行） */
  const curLine = photo ? (fromLineId && photo.lineIds.includes(fromLineId) ? fromLineId : (photo.lineIds[0] ?? null)) : null;
  const names = photo ? photo.lineIds.map((x) => lineName(x)).filter(Boolean).join("・") : "";
  return (
    <div className="ph-viewer" role="dialog" aria-modal="true" aria-label="写真" data-testid="phone-viewer">
      <div className="ph-viewtop">
        <button type="button" className="ph-iconbtn light" aria-label={PHONE_MSG.close} onClick={() => { sendCaption.flush(); onClose(); }} data-testid="phone-viewer-close">
          <PhIcon k="x" />
        </button>
        {names ? <span className="ln">{names}</span> : null}
      </div>
      <div className="ph-viewimg">
        <img src={photo ? photo.url : pendingItem!.preview} alt={photo?.caption || "写真"} />
      </div>
      {photo ? (
        <div className="ph-viewbar">
          <div className="field">
            <input id="ph-caption" className="inp" value={caption} placeholder=" " maxLength={200} onChange={(e) => { setCaption(e.target.value); sendCaption.push(e.target.value); }} onBlur={sendCaption.flush} data-testid="phone-caption" />
            <label htmlFor="ph-caption">{PHONE_MSG.caption}</label>
          </div>
          <ErrorText>{err}</ErrorText>
          <div className="ph-actions">
            <button type="button" className="btn tonal" onClick={() => setPicking(true)} data-testid="phone-photo-move">
              {curLine ? PHONE_MSG.moveTo : PHONE_MSG.toLine}
            </button>
            <span className="sp" />
            <button
              type="button"
              className={"btn" + (sure ? " ph-danger" : " text danger")}
              onClick={async () => {
                if (!sure) {
                  setSure(true);
                  setTimeout(() => setSure(false), 4000);
                  return;
                }
                try {
                  const r = await api<LinePhotoList>("DELETE", `/estimates/${estimateId}/photos/${photo.id}`);
                  onChanged(r, PHONE_MSG.photoRemoved);
                  onClose();
                } catch (e) {
                  setErr(messageOf(e));
                }
              }}
              data-testid="phone-photo-delete"
            >
              <PhIcon k="trash" />
              {sure ? "削除（元に戻せません）" : PHONE_MSG.removePhoto}
            </button>
          </div>
        </div>
      ) : (
        <div className="ph-viewbar">
          <p className="ph-note">{PHONE_MSG.pendingHelp}</p>
        </div>
      )}
      {picking && photo ? (
        <LinePickerSheet
          groups={groups}
          current={curLine}
          onClose={() => setPicking(false)}
          onPick={async (lineId) => {
            setPicking(false);
            await send({ lineId, ...(curLine ? { fromLineId: curLine } : {}) }, lineId ? fill(PHONE_MSG.photoMoved, { name: lineName(lineId) }) : PHONE_MSG.photoUnassigned);
            onClose();
          }}
        />
      ) : null}
    </div>
  );
}

/** どの行に入れるか（見出しの帯ごと。行に入れない も選べる） */
function LinePickerSheet({ groups, current, onPick, onClose }: { groups: Group[]; current: string | null; onPick: (lineId: string | null) => void; onClose: () => void }) {
  return (
    <SheetFrame title={PHONE_MSG.moveToTitle} onClose={onClose} full z={90} testId="phone-line-picker">
      <ul className="ph-candlist">
        <li>
          <button type="button" className="ph-cand" aria-pressed={current == null} onClick={() => onPick(null)} data-testid="phone-pick-none">
            <b>{PHONE_MSG.noLine}</b>
          </button>
        </li>
      </ul>
      {groups.map((g) => {
        const rows = g.rows.filter((r) => isPhoneRole(r.role) && r.role !== "group");
        if (!rows.length) return null;
        return (
          <div key={g.key}>
            <b className="ph-sub">{g.name}</b>
            <ul className="ph-candlist">
              {rows.map((r) => (
                <li key={r.id}>
                  <button type="button" className="ph-cand" aria-pressed={current === r.id} onClick={() => onPick(r.id)} data-testid="phone-pick-line">
                    <b>{r.name || "（名前なし）"}</b>
                    {r.spec ? <span className="m">{r.spec}</span> : null}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </SheetFrame>
  );
}

/** 見積全体の現場メモ（施主の要望など行にならないこと。紙に出ない） */
function NoteSheet({ estimateId, onClose, onSaved }: { estimateId: string; onClose: () => void; onSaved: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["estimate-note", estimateId], queryFn: () => api<EstimateNote>("GET", `/estimates/${estimateId}/note`) });
  const [body, setBody] = useState<string | null>(null);
  const [err, setErr] = useState<string | undefined>();
  const value = body ?? q.data?.body ?? "";
  return (
    <SheetFrame title={PHONE_MSG.siteNote} onClose={onClose} full testId="phone-note">
      <p className="ph-note">{t(PHONE_MSG.siteNoteHelp)}</p>
      <textarea className="inp ph-notearea" value={value} maxLength={2000} rows={8} aria-label={PHONE_MSG.siteNote} onChange={(e) => setBody(e.target.value)} data-testid="phone-note-body" />
      <ErrorText>{err}</ErrorText>
      <button
        type="button"
        className="btn primary ph-wide"
        disabled={!q.data}
        onClick={async () => {
          try {
            const r = await api<EstimateNote>("PUT", `/estimates/${estimateId}/note`, { body: value });
            qc.setQueryData(["estimate-note", estimateId], r);
            onSaved();
            onClose();
          } catch (e) {
            setErr(messageOf(e));
          }
        }}
        data-testid="phone-note-save"
      >
        {PHONE_MSG.saveNote}
      </button>
    </SheetFrame>
  );
}

/** ⋮：PC版で表示／見積のメモ／下書きを削除 */
function MenuSheet({ canDiscard, onPc, onNote, onDiscard, onClose }: { canDiscard: boolean; onPc: () => void; onNote: () => void; onDiscard: () => void; onClose: () => void }) {
  return (
    <SheetFrame title={PHONE_MSG.more} onClose={onClose} testId="phone-menu">
      <ul className="ph-menu" role="menu">
        <li>
          <button type="button" role="menuitem" onClick={onPc} data-testid="phone-menu-pc">{PHONE_MSG.openPc}</button>
        </li>
        <li>
          <button type="button" role="menuitem" onClick={onNote} data-testid="phone-menu-note">{PHONE_MSG.siteNote}</button>
        </li>
        {canDiscard ? (
          <li>
            <button type="button" role="menuitem" className="shu" onClick={onDiscard} data-testid="phone-menu-discard">{PHONE_MSG.discard}</button>
          </li>
        ) : null}
      </ul>
    </SheetFrame>
  );
}

function ConfirmSheet({ title, text, okLabel, onOk, onClose }: { title: string; text: string; okLabel: string; onOk: () => Promise<void>; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <SheetFrame title={title} onClose={onClose} testId="phone-confirm">
      <p className="ph-note">{text}</p>
      <div className="ph-actions">
        <button type="button" className="btn" onClick={onClose} autoFocus>取消</button>
        <span className="sp" />
        <button
          type="button"
          className="btn ph-danger"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            await onOk();
            setBusy(false);
          }}
          data-testid="phone-confirm-ok"
        >
          {okLabel}
        </button>
      </div>
    </SheetFrame>
  );
}
